// src/pages/CompetitorAnalysis/CompetitorAnalysisDashboard.tsx
// TODO: Remove "Coming Soon" and uncomment imports below when ready
import React from 'react';
import styles from './CompetitorAnalysisDashboard.module.css';

export const CompetitorAnalysisDashboard: React.FC = () => {
  return (
    <div className={styles.dashboard}>
      <div className={styles.comingSoonBanner}>
        <span className={styles.comingSoonIcon}>🚧</span>
        <h2 className={styles.comingSoonTitle}>Coming Soon</h2>
        <p className={styles.comingSoonText}>
          Competitor Analysis is under development. Stay tuned!
        </p>
      </div>
    </div>
  );
};

/*
// ─── ORIGINAL IMPORTS & COMPONENT (uncomment when ready) ─────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSimilarityProjects } from '../../hooks/useSimilarityProjects';
import { useCompetitorAnalysis } from '../../hooks/useCompetitorAnalysis';
import { CompetitorOverview } from '../../components/CompetitorAnalysis/CompetitorOverview';
import { CompetitorDiscovery } from '../../components/CompetitorAnalysis/CompetitorDiscovery';
import { ManualCompetitorEntry } from '../../components/CompetitorAnalysis/ManualCompetitorEntry';
import { CompetitorJobsList } from '../../components/CompetitorAnalysis/CompetitorJobsList';
import { CompetitorJobDetails } from '../../components/CompetitorAnalysis/CompetitorJobDetails';
import type { CompetitorAnalysisJob } from '../../types/competitor-analysis';
import type { SimilarityProject } from '../../types/similarity';
import styles from './CompetitorAnalysisDashboard.module.css';

type ViewMode = 'overview' | 'select-method' | 'discovery' | 'manual' | 'jobs' | 'job-details';

export const CompetitorAnalysisDashboard: React.FC = () => {
  const { projectId, jobId } = useParams<{ projectId?: string; jobId?: string }>();
  const navigate = useNavigate();

  const { projects, createNewProject } = useSimilarityProjects();
  const { getJobStatus, startPollingJob } = useCompetitorAnalysis();

  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [selectedProject, setSelectedProject] = useState<SimilarityProject | null>(null);
  const [selectedJob, setSelectedJob] = useState<CompetitorAnalysisJob | null>(null);
  const [jobs, setJobs] = useState<CompetitorAnalysisJob[]>([]);

  // Determine view mode based on URL params
  useEffect(() => {
    console.log('🔍 URL params:', { projectId, jobId });
    if (jobId) {
      console.log('📊 Setting view mode to: job-details');
      setViewMode('job-details');
    } else if (projectId) {
      console.log('📊 Setting view mode to: jobs');
      setViewMode('jobs');
    } else {
      console.log('📊 Setting view mode to: overview');
      setViewMode('overview');
    }
  }, [projectId, jobId]);

  // Set selected project based on projectId param
  useEffect(() => {
    console.log('🔍 Projects loaded:', projects.length);
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        console.log('✅ Project found:', project);
        setSelectedProject(project);
      } else {
        console.warn(`❌ Project ${projectId} not found`);
        // Navigate back to method selection
        navigate('/competitor-analysis');
      }
    }
  }, [projectId, projects, navigate]);

  // Load job details when jobId changes
  useEffect(() => {
    if (jobId && viewMode === 'job-details') {
      loadJobDetails(jobId);
    }
  }, [jobId, viewMode]);

  const loadJobDetails = useCallback(async (id: string) => {
    const job = await getJobStatus(id);
    if (job) {
      setSelectedJob(job);

      // Start polling if job is not finished
      if (job.status === 'queued' || job.status === 'running') {
        startPollingJob(id, (updatedJob) => {
          setSelectedJob(updatedJob);
        });
      }
    }
  }, [getJobStatus, startPollingJob]);

  const handleCreateNew = useCallback(() => {
    setViewMode('select-method');
  }, []);

  const handleMethodSelect = useCallback((method: 'discovery' | 'manual') => {
    setViewMode(method);
  }, []);

  const handleBackToOverview = useCallback(() => {
    setViewMode('overview');
    navigate('/competitor-analysis');
  }, [navigate]);

  const handleBackToMethodSelection = useCallback(() => {
    setViewMode('select-method');
  }, []);

  const handleJobCreated = useCallback((job: CompetitorAnalysisJob) => {
    // Add job to list
    setJobs(prev => [job, ...prev]);

    // Navigate back to overview to see the new job
    navigate('/competitor-analysis');
    setViewMode('overview');

    // Start polling the new job
    startPollingJob(job.id, (updatedJob) => {
      setJobs(prev => prev.map(j => j.id === updatedJob.id ? updatedJob : j));
    });
  }, [navigate, startPollingJob]);

  const handleViewJob = useCallback((job: CompetitorAnalysisJob, projectId?: string) => {
    navigate(`/competitor-analysis/${projectId || job.project_id}/jobs/${job.id}`);
  }, [navigate]);

  const handleBackToJobs = useCallback(() => {
    // Always go back to the main overview where all analyses are shown
    navigate('/competitor-analysis');
    setViewMode('overview');
  }, [navigate]);

  // Ensure user has at least one project
  const handleEnsureProject = useCallback(async (): Promise<SimilarityProject | null> => {
    if (selectedProject) {
      return selectedProject;
    }

    // Use first available project or create one
    if (projects.length > 0) {
      const project = projects[0];
      setSelectedProject(project);
      return project;
    }

    // Create a default project
    const newProject = await createNewProject({
      name: 'Competitor Analysis',
      description: 'Content gap analysis and competitor research',
    });

    if (newProject) {
      setSelectedProject(newProject);
      return newProject;
    }

    return null;
  }, [selectedProject, projects, createNewProject]);

  return (
    <div className={styles.dashboard}>
      <div className={styles.comingSoonBanner}>
        <span className={styles.comingSoonIcon}>🚧</span>
        <h2 className={styles.comingSoonTitle}>Coming Soon</h2>
        <p className={styles.comingSoonText}>
          Competitor Analysis is under development. Stay tuned!
        </p>
      </div>
    </div>
  );
};


*/